/**
 * Position of live price between a point's stop-loss, entry and take-profit.
 *
 * SL maps to 0, entry to 50 and TP to 100 on the bar, whichever side the trade is on — each half
 * is normalised on its own, so this is neither a linear price scale nor a risk/reward chart.
 * Only a live reading is computed here; whether a trade is open comes from the backend state,
 * never from price crossing the entry.
 *
 * @param {{side: 'LONG'|'SHORT', state: 'PENDING'|'OPEN'|'CANCELED'|'CLOSED', entry: number,
 *   stopLoss: number, takeProfit: number, price: ?number, tolerance: number}} input
 * @return {object}
 */
export function calculateMeter({ side, state, entry, stopLoss, takeProfit, price, tolerance, outcome = null }) {
  // A point that closed on its take-profit or stop-loss: the marker sits at that end of the bar.
  if (state === 'CLOSED' && (outcome === 'profit' || outcome === 'loss')) {
    return { mode: 'settled', outcome, edgePosition: outcome === 'profit' ? 100 : 0 }
  }

  if (state === 'CANCELED' || state === 'CLOSED') {
    return { mode: 'inactive', state }
  }

  const valid = [entry, stopLoss, takeProfit].every(v => Number.isFinite(v) && v > 0)
  if (!valid || !Number.isFinite(tolerance) || tolerance < 0) {
    return { mode: 'invalid' }
  }

  const direction = side === 'LONG' ? 1 : -1
  const slDistance = direction * (stopLoss - entry)
  const tpDistance = direction * (takeProfit - entry)
  if (slDistance >= 0 || tpDistance <= 0) {
    return { mode: 'invalid' }
  }

  if (price === null || price === undefined || !Number.isFinite(price) || price <= 0) {
    return { mode: 'missing-price' }
  }

  const delta = direction * (price - entry)
  const neutral = Math.abs(price - entry) <= tolerance
  const rawPosition = neutral
    ? 50
    : delta < 0
      ? 50 + (50 * delta) / Math.abs(slDistance)
      : 50 + (50 * delta) / tpDistance
  const overflow = rawPosition < 0 ? 'SL' : rawPosition > 100 ? 'TP' : null
  const tone = state === 'PENDING' ? 'pending' : neutral ? 'neutral' : delta > 0 ? 'profit' : 'loss'

  return {
    mode: 'live',
    rawPosition,
    edgePosition: Math.max(0, Math.min(100, rawPosition)),
    overflow,
    tone,
    markerStyle: state === 'PENDING' ? 'outline' : 'filled',
    signedDistancePct: neutral ? 0 : (delta / entry) * 100,
    absoluteDistancePct: neutral ? 0 : Math.abs((delta / entry) * 100),
    // A non-zero move smaller than the shown precision must not render as "-0.00%".
    belowPrecision: !neutral && Math.abs((delta / entry) * 100) < 0.005,
  }
}

/**
 * The percent label: signed for an open trade, absolute while waiting for entry.
 *
 * @param {object} meter  result of calculateMeter() in live mode
 * @param {string} state  PENDING | OPEN
 * @param {boolean} persian  Persian digits, decimal separator and percent sign
 * @return {string}
 */
export function formatMeterPercent(meter, state, persian = false) {
  const number = value =>
    persian
      ? new Intl.NumberFormat('fa-IR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
      : value.toFixed(2)
  const percent = persian ? '٪' : '%'

  if (meter.tone === 'neutral') return `${number(0)}${percent}`

  if (meter.belowPrecision) {
    const sign = state === 'OPEN' ? (meter.signedDistancePct > 0 ? '+' : '−') : ''
    const floor = persian ? '<۰٫۰۱' : '<0.01'

    return `${sign}${floor}${percent}`
  }

  if (state === 'OPEN') {
    const sign = meter.signedDistancePct > 0 ? '+' : '−'

    return `${sign}${number(Math.abs(meter.signedDistancePct))}${percent}`
  }

  return `${number(meter.absoluteDistancePct)}${percent}`
}
